import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Clock, CheckCircle2, XCircle, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EXECUTIVE_IDS = [
  "bb385f23-ed54-4a65-ba02-8b80b567bf62",
  "af9054fe-4735-4981-85d8-1de34c32da12",
  "528b9027-60d4-41f9-bffc-283fa61e4efb",
];

interface Props {
  lessonId: string;
  variant: "portal" | "intranet";
}

const statusLabels: Record<string, { label: string; icon: typeof Clock; color: string }> = {
  open: { label: "Open", icon: MessageSquare, color: "text-blue-600" },
  pending_approval: { label: "Pending Approval", icon: Clock, color: "text-amber-600" },
  approved: { label: "Answered", icon: CheckCircle2, color: "text-emerald-600" },
  closed: { label: "Closed", icon: XCircle, color: "text-muted-foreground" },
};

export const LessonCommentThread = ({ lessonId, variant }: Props) => {
  const { user, fullName, isEmployee, companyId } = useAuth();
  const qc = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");

  const isExec = user?.id ? EXECUTIVE_IDS.includes(user.id) : false;
  const isPortal = variant === "portal";

  const { data: comments = [] } = useQuery({
    queryKey: ["lesson-comments", lessonId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_comments" as any)
        .select("*")
        .eq("lesson_id", lessonId)
        .order("created_at", { ascending: true });
      return (data ?? []) as any[];
    },
  });

  const { data: approvals = [] } = useQuery({
    queryKey: ["lesson-approvals", lessonId],
    enabled: !isPortal,
    queryFn: async () => {
      const commentIds = comments.filter((c: any) => c.status === "pending_approval").map((c: any) => c.id);
      if (commentIds.length === 0) return [];
      const { data } = await supabase
        .from("lesson_comment_approvals" as any)
        .select("*")
        .in("comment_id", commentIds);
      return (data ?? []) as any[];
    },
  });

  const askQuestion = useMutation({
    mutationFn: async (content: string) => {
      const { error } = await supabase.from("lesson_comments" as any).insert({
        lesson_id: lessonId,
        author_id: user!.id,
        author_name: fullName || user!.email,
        author_type: isPortal ? "client" : "employee",
        content: content.trim(),
        status: "open",
        company_id: companyId,
      });
      if (error) throw error;

      if (isPortal) {
        const { data: companies } = await supabase.from("companies").select("id").limit(1);
        if (companies?.[0]) {
          await supabase.from("notifications").insert({
            company_id: companies[0].id,
            type: "question_answered",
            title: `New question on Lesson ${lessonId}`,
            message: `${fullName || "A client"} asked: "${content.trim().slice(0, 100)}..."`,
          });
        }
      }
    },
    onSuccess: () => {
      setNewComment("");
      qc.invalidateQueries({ queryKey: ["lesson-comments", lessonId] });
      toast.success(isPortal ? "Question submitted — Velocity Offshore will respond shortly." : "Comment posted.");
    },
    onError: () => toast.error("Failed to post comment."),
  });

  const replyToComment = useMutation({
    mutationFn: async ({ parentId, content }: { parentId: string; content: string }) => {
      const { data: inserted, error } = await supabase.from("lesson_comments" as any).insert({
        lesson_id: lessonId,
        parent_comment_id: parentId,
        author_id: user!.id,
        author_name: fullName || user!.email,
        author_type: "employee",
        content: content.trim(),
        status: "pending_approval",
        company_id: null,
      }).select().single();
      if (error) throw error;

      for (const execId of EXECUTIVE_IDS) {
        await supabase.from("lesson_comment_approvals" as any).insert({
          comment_id: (inserted as any).id,
          approver_id: execId,
          approver_name: execId === "bb385f23-ed54-4a65-ba02-8b80b567bf62" ? "Bo" : execId === "af9054fe-4735-4981-85d8-1de34c32da12" ? "Jerome" : "Kyle",
          decision: "pending",
        });
      }
    },
    onSuccess: () => {
      setReplyTo(null);
      setReplyContent("");
      qc.invalidateQueries({ queryKey: ["lesson-comments", lessonId] });
      toast.success("Reply submitted for executive approval.");
    },
    onError: () => toast.error("Failed to send reply."),
  });

  const approveComment = useMutation({
    mutationFn: async (commentId: string) => {
      await supabase.from("lesson_comment_approvals" as any)
        .update({ decision: "approved" })
        .eq("comment_id", commentId)
        .eq("approver_id", user!.id);

      const { data: allApprovals } = await supabase
        .from("lesson_comment_approvals" as any)
        .select("decision")
        .eq("comment_id", commentId);

      const allApproved = (allApprovals ?? []).every((a: any) => a.decision === "approved");
      if (allApproved) {
        const { data: comment } = await supabase
          .from("lesson_comments" as any)
          .select("*, parent:parent_comment_id(company_id)")
          .eq("id", commentId)
          .single();

        await supabase.from("lesson_comments" as any)
          .update({ status: "approved" })
          .eq("id", commentId);

        const parentCompanyId = (comment as any)?.parent?.company_id;
        if (parentCompanyId) {
          await supabase.from("notifications").insert({
            company_id: parentCompanyId,
            type: "question_answered",
            title: `Response on Lesson ${lessonId}`,
            message: `Velocity Offshore responded to your question on lesson ${lessonId}.`,
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lesson-comments", lessonId] });
      qc.invalidateQueries({ queryKey: ["lesson-approvals", lessonId] });
      toast.success("Approval recorded.");
    },
  });

  const reopenComment = useMutation({
    mutationFn: async (commentId: string) => {
      await supabase.from("lesson_comments" as any)
        .update({ status: "open", updated_at: new Date().toISOString() })
        .eq("id", commentId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lesson-comments", lessonId] });
      toast.success("Comment reopened.");
    },
  });

  const closeComment = useMutation({
    mutationFn: async (commentId: string) => {
      await supabase.from("lesson_comments" as any)
        .update({ status: "closed", updated_at: new Date().toISOString() })
        .eq("id", commentId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lesson-comments", lessonId] });
      toast.success("Comment closed.");
    },
  });

  const topLevel = comments.filter((c: any) => !c.parent_comment_id);
  const getReplies = (parentId: string) => comments.filter((c: any) => c.parent_comment_id === parentId);

  const commentCount = comments.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <MessageSquare className={`w-4 h-4 ${isPortal ? "text-[hsl(211,100%,40%)]" : "text-teal"}`} />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Discussion ({commentCount})
        </span>
      </div>

      {topLevel.map((comment: any) => {
        const stCfg = statusLabels[comment.status] || statusLabels.open;
        const replies = getReplies(comment.id);
        const commentApprovals = approvals.filter((a: any) => a.comment_id === comment.id || replies.some((r: any) => r.id === a.comment_id));

        return (
          <div key={comment.id} className={`rounded-lg border p-3 space-y-2 ${
            isPortal ? "border-[hsl(220,15%,90%)] bg-[hsl(220,20%,98%)]" : "border-border bg-muted/30"
          }`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-foreground">{comment.author_name}</span>
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                    {comment.author_type === "client" ? "Client" : "Employee"}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(comment.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-foreground/80">{comment.content}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <stCfg.icon className={`w-3.5 h-3.5 ${stCfg.color}`} />
                <span className={`text-[10px] font-medium ${stCfg.color}`}>{stCfg.label}</span>
              </div>
            </div>

            {replies.map((reply: any) => {
              const rStCfg = statusLabels[reply.status] || statusLabels.open;
              const replyApprovals = approvals.filter((a: any) => a.comment_id === reply.id);

              return (
                <div key={reply.id} className={`ml-4 pl-3 border-l-2 ${
                  isPortal ? "border-[hsl(211,100%,40%)]/20" : "border-teal/20"
                }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-foreground">{reply.author_name}</span>
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0">
                          {reply.author_type === "client" ? "Client" : "Velocity Offshore"}
                        </Badge>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(reply.created_at).toLocaleDateString()}
                        </span>
                        {reply.status === "pending_approval" && !isPortal && (
                          <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-700">
                            <Clock className="w-2.5 h-2.5 mr-1" /> Awaiting Exec Approval
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80">{reply.content}</p>

                      {!isPortal && reply.status === "pending_approval" && replyApprovals.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          {replyApprovals.map((a: any) => (
                            <div key={a.id} className="flex items-center gap-1">
                              <div className={`w-2 h-2 rounded-full ${a.decision === "approved" ? "bg-emerald-500" : a.decision === "rejected" ? "bg-red-500" : "bg-amber-400"}`} />
                              <span className="text-[10px] text-muted-foreground">{a.approver_name}</span>
                            </div>
                          ))}
                          {isExec && replyApprovals.some((a: any) => a.approver_id === user?.id && a.decision === "pending") && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] ml-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => approveComment.mutate(reply.id)}>
                              <ShieldCheck className="w-3 h-3 mr-1" /> Approve
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex items-center gap-2 pt-1">
              {!isPortal && comment.status === "open" && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}>
                  <Send className="w-3 h-3 mr-1" /> Reply
                </Button>
              )}
              {isPortal && comment.author_type === "client" && comment.status === "approved" && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}>
                  <Send className="w-3 h-3 mr-1" /> Reply
                </Button>
              )}
              {comment.status !== "closed" && (comment.author_id === user?.id || isExec || !isPortal) && (
                <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => closeComment.mutate(comment.id)}>
                  <XCircle className="w-3 h-3 mr-1" /> Close
                </Button>
              )}
              {comment.status === "closed" && (
                <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600" onClick={() => reopenComment.mutate(comment.id)}>
                  <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                </Button>
              )}
            </div>

            {replyTo === comment.id && (
              <div className="ml-4 pl-3 border-l-2 border-dashed border-muted-foreground/20 space-y-2">
                <Textarea
                  value={replyContent}
                  onChange={e => setReplyContent(e.target.value)}
                  placeholder={isPortal ? "Reply to this thread..." : "Reply (requires executive approval before client sees it)..."}
                  rows={3}
                  className="text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (replyContent.trim()) {
                        if (isPortal) {
                          askQuestion.mutate(replyContent);
                        } else {
                          replyToComment.mutate({ parentId: comment.id, content: replyContent });
                        }
                      }
                    }}
                    disabled={!replyContent.trim()}
                    className={isPortal ? "bg-[hsl(211,100%,40%)] hover:bg-[hsl(211,100%,45%)] text-white" : "bg-teal hover:bg-teal/90 text-white"}
                  >
                    <Send className="w-3 h-3 mr-1" /> Send
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setReplyTo(null); setReplyContent(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="space-y-2">
        <Textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder={isPortal ? "Ask a question about this lesson learned..." : "Add a comment..."}
          rows={2}
          className="text-sm"
        />
        <Button
          size="sm"
          onClick={() => newComment.trim() && askQuestion.mutate(newComment)}
          disabled={!newComment.trim() || askQuestion.isPending}
          className={isPortal ? "bg-[hsl(211,100%,40%)] hover:bg-[hsl(211,100%,45%)] text-white" : "bg-teal hover:bg-teal/90 text-white"}
        >
          <MessageSquare className="w-3.5 h-3.5 mr-1" /> {isPortal ? "Ask Question" : "Post Comment"}
        </Button>
      </div>
    </div>
  );
};
